import { useContext } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import type { SuggestionResult } from '../core/suggestions/getSuggestions';
import type { ActiveModalWithKey } from '../contexts/ModalContext';
import type { RegionBalanceFilter } from '../timeline/Regions';
import TimelinePreferencesContext, {
  DEFAULT_ZOOM_WEEKS,
} from '../contexts/TimelinePreferencesContext';
import type { EventTimeline } from '../core/Timelines';
import type { UseScenariosResult } from '../Scenario';

type PlanningHotkeysListenerProps = {
  activeModals: ActiveModalWithKey[];
  filteredOrderIds: Set<number>;
  handleUnassignSelectedOrders: () => void;
  handleZoomToVoyages: (orderIds?: number[]) => void;
  setCurrentTimelineMode: (mode: 'plan' | 'commit') => void;
  setRegionBalanceFilter: (filter: RegionBalanceFilter) => void;
  setSelectedOrderIds: (ids: number[]) => void;
  setSuggestions: (suggestions: SuggestionResult | undefined) => void;
  suggestions: SuggestionResult | undefined;
  timelineSelection: {
    selectedOrderIds: Set<number>;
    selectedEventIds: Set<string | number>;
  };
  timelines: EventTimeline[];
  undo: UseScenariosResult['undo'];
};

export function PlanningHotkeysListener({
  activeModals,
  filteredOrderIds,
  handleUnassignSelectedOrders,
  handleZoomToVoyages,
  setCurrentTimelineMode,
  setRegionBalanceFilter,
  setSelectedOrderIds,
  setSuggestions,
  suggestions,
  timelineSelection,
  timelines,
  undo,
}: PlanningHotkeysListenerProps) {
  const { setZoomLevel } = useContext(TimelinePreferencesContext);

  // Zoom all voyages
  useHotkeys(
    'meta+1, ctrl+1',
    () => {
      handleZoomToVoyages();
    },
    [timelines],
    { preventDefault: true },
  );

  // Zoom to 2 weeks
  useHotkeys(
    'meta+2, ctrl+2',
    () => {
      setZoomLevel(2);
    },
    { preventDefault: true },
  );

  // Reset zoom level
  useHotkeys(
    'meta+3, ctrl+3',
    () => {
      setZoomLevel(DEFAULT_ZOOM_WEEKS);
    },
    { preventDefault: true },
  );

  // Zoom to 4 weeks
  useHotkeys(
    'meta+4, ctrl+4',
    () => {
      setZoomLevel(4);
    },
    { preventDefault: true },
  );

  // Zoom to selected voyages
  useHotkeys(
    'meta+5, ctrl+5',
    () => {
      handleZoomToVoyages(Array.from(timelineSelection.selectedOrderIds));
    },
    [handleZoomToVoyages, timelines, timelineSelection],
    { preventDefault: true },
  );

  // Select all
  useHotkeys(
    'meta+a, ctrl+a',
    () => {
      if (activeModals.length < 1) {
        setSelectedOrderIds(Array.from(filteredOrderIds));
      }
    },
    [activeModals, setSelectedOrderIds, filteredOrderIds],
    { preventDefault: true },
  );

  // Clear selection/suggestions
  useHotkeys(
    'esc',
    () => {
      if (suggestions) {
        setSuggestions(undefined);
      } else {
        setRegionBalanceFilter([]);
      }
    },
    [setRegionBalanceFilter, setSuggestions, suggestions],
    { preventDefault: true },
  );
  useHotkeys('esc', () => {
    setCurrentTimelineMode('plan');
  });

  // Unassign orders
  useHotkeys(
    'backspace',
    () => {
      if (activeModals.length < 1) {
        handleUnassignSelectedOrders();
      }
    },
    [activeModals, timelineSelection],
    { preventDefault: true },
  );

  // Undo
  useHotkeys(
    'meta+z, ctrl+z',
    () => {
      if (undo.canUndo) {
        undo.undo();
      }
    },
    [undo.canUndo, undo.undo],
  );

  // Redo
  useHotkeys(
    'meta+shift+z, ctrl+shift+z',
    () => {
      if (undo.canRedo) {
        undo.redo();
      }
    },
    [undo.canRedo, undo.redo],
  );

  return null;
}
